import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

const languages = [
  { code: "en", label: "English", short: "EN" },
  { code: "fr", label: "Français", short: "FR" },
  { code: "da", label: "Dansk", short: "DA" },
  { code: "de", label: "Deutsch", short: "DE" },
];

interface Props {
  light?: boolean;
}

export default function LanguageSwitcher({ light = false }: Props) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current =
    languages.find((l) => l.code === i18n.language) ?? languages[0];

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  function select(code: string) {
    i18n.changeLanguage(code);
    setOpen(false);
  }

  const btnColor = light
    ? "text-background-50 hover:bg-background-50/20"
    : "text-foreground-700 hover:bg-background-100";

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium whitespace-nowrap cursor-pointer ${btnColor}`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="w-4 h-4 flex items-center justify-center">
          <i className="ri-global-line" />
        </span>
        <span>{current.short}</span>
        <span className="w-3 h-3 flex items-center justify-center">
          <i className="ri-arrow-down-s-line" />
        </span>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-40 rounded-lg border border-background-200 bg-background-50 py-1 z-50">
          {languages.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => select(l.code)}
              className={`flex w-full items-center justify-between px-4 py-2 text-left text-sm whitespace-nowrap cursor-pointer hover:bg-background-100 ${
                l.code === current.code
                  ? "text-primary-700 font-semibold"
                  : "text-foreground-700"
              }`}
            >
              <span>{l.label}</span>
              {l.code === current.code && (
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-check-line" />
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}